import React, { FC } from 'react';
import styled from 'styled-components';
import { Spin, Alert, Select, Empty, Button } from 'antd';
import { ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import MappingTable from './MappingTable';
import { IBcfMappingConfig } from 'Data/BcfImport.api';

interface IStep2DataMappingProps {
  mappingConfigs: IBcfMappingConfig;
  analysisResult: any;
  isLoading: boolean;
  isLoadingDefaults: boolean;
  defaultsError: string | undefined;
  error: string | undefined;
  availableDefaults: any;
  onMappingChange: (fieldType: string, bcfValue: string, coordlyValue: string | number) => void;
  onDefaultValueChange?: (fieldType: string, defaultValue: string | number) => void;
  onRetryLoadDefaults?: () => void;
  onIgnoreChange?: (fieldType: string, isIgnored: boolean) => void;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const Description = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const MappingSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 24px;
`;

const DefaultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 8px;
  border-left: 4px solid ${({ theme }) => theme.colors.warning.warning};
`;

const DefaultsSectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const DefaultsSectionDescription = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const DefaultFieldRow = styled.div`
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px;
  align-items: center;
`;

const DefaultFieldLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.surface.onSurface};
`;

const RequiredMark = styled.span`
  color: #ef4444;
  margin-left: 2px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  gap: 16px;
`;

const LoadingText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 8px;
`;

// Field configurations with bcfKey for BCF values
const MappingFields = [
  {
    key: 'status',
    label: 'Status',
    description: 'Vincule os status do BCF com os status da Coordly',
    defaultKey: 'TopicStatus',
    bcfKey: 'statuses',
    required: true,
  },
  {
    key: 'type',
    label: 'Tipos',
    description: 'Vincule os tipos do BCF com os tipos da Coordly',
    defaultKey: 'TopicTypes',
    bcfKey: 'types',
    required: true,
  },
  {
    key: 'priority',
    label: 'Prioridades',
    description: 'Vincule as prioridades do BCF com as prioridades da Coordly',
    defaultKey: 'TopicPriorities',
    bcfKey: 'priorities',
    required: false,
  },
  {
    key: 'user',
    label: 'Usuários',
    description: 'Vincule os usuários do BCF com os usuários da Coordly',
    defaultKey: 'Users',
    bcfKey: 'users',
    required: false,
  },
  {
    key: 'stage',
    label: 'Etapas',
    description: 'Vincule as etapas do BCF com as etapas da Coordly',
    defaultKey: 'TopicStages',
    bcfKey: 'stages',
    required: false,
  },
  {
    key: 'labels',
    label: 'Disciplinas',
    description: 'Vincule as disciplinas do BCF com as disciplinas da Coordly',
    defaultKey: 'ConstructionSiteDisciplines',
    bcfKey: 'labels',
    required: false,
  },
];

// Helper to get ID from default value (PascalCase)
const getValueId = (val: any) => {
  return val.TopicStatusId || val.TopicTypeId || val.TopicStageId ||
    val.TopicPriorityId || val.ApplicationUserId ||
    val.ConstructionSiteDisciplineId || val.Id;
};

const getValueName = (val: any) => {
  return val.Name || val.FullName || val.Email;
};

const Step2DataMapping: FC<IStep2DataMappingProps> = ({
  mappingConfigs,
  analysisResult,
  isLoading,
  isLoadingDefaults,
  defaultsError,
  error,
  availableDefaults,
  onMappingChange,
  onDefaultValueChange,
  onRetryLoadDefaults,
  onIgnoreChange,
}) => {
  // Show loading state when fetching defaults
  if (isLoadingDefaults) {
    return (
      <Container>
        <Header>
          <Title>Vincule os dados</Title>
          <Description>Carregando dados disponíveis...</Description>
        </Header>
        <LoadingContainer>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
          <LoadingText>Buscando status, tipos, usuários e etapas...</LoadingText>
        </LoadingContainer>
      </Container>
    );
  }

  // Show error state with retry button
  if (defaultsError && !availableDefaults) {
    return (
      <Container>
        <Header>
          <Title>Vincule os dados</Title>
          <Description>Mapeie os valores do BCF com os valores da Coordly</Description>
        </Header>
        <ErrorContainer>
          <Alert
            message="Erro ao carregar dados"
            description={defaultsError}
            type="error"
            showIcon
          />
          {onRetryLoadDefaults && (
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={onRetryLoadDefaults}
            >
              Tentar Novamente
            </Button>
          )}
        </ErrorContainer>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Spin tip="Carregando dados..." />
      </Container>
    );
  }

  // Get BCF values from analysis result
  const bcfValues = analysisResult?.bcfValues || {};

  // Filter fields that have values in BCF
  const fieldsWithValues = MappingFields.filter((field) => {
    const values = bcfValues[field.bcfKey] || [];
    return values.length > 0;
  });

  // Find required fields without BCF values (need default values)
  const requiredFieldsWithoutValues = MappingFields.filter((field) => {
    const values = bcfValues[field.bcfKey] || [];
    return field.required && values.length === 0;
  });

  return (
    <Container>
      <Header>
        <Title>Vincule os dados</Title>
        <Description>Mapeie os valores do BCF com os valores da Coordly</Description>
      </Header>

      {error && <Alert message={error} type="error" showIcon />}

      {/* Show default value selectors for required fields not in BCF */}
      {requiredFieldsWithoutValues.length > 0 && (
        <DefaultsSection>
          <DefaultsSectionTitle>Valores Padrão Obrigatórios</DefaultsSectionTitle>
          <DefaultsSectionDescription>
            Os seguintes campos são obrigatórios mas não foram encontrados no arquivo BCF.
            Selecione valores padrão para eles:
          </DefaultsSectionDescription>
          {requiredFieldsWithoutValues.map((field) => {
            const defaults = (availableDefaults as any)?.[field.defaultKey] || [];
            return (
              <DefaultFieldRow key={field.key}>
                <DefaultFieldLabel>
                  {field.label}:<RequiredMark>*</RequiredMark>
                </DefaultFieldLabel>
                <Select
                  placeholder={`Selecione ${field.label.toLowerCase()} padrão`}
                  style={{ width: '100%' }}
                  onChange={(value) => onDefaultValueChange?.(field.key, value)}
                  options={defaults.map((val: any) => ({
                    label: getValueName(val),
                    value: getValueId(val),
                  }))}
                />
              </DefaultFieldRow>
            );
          })}
        </DefaultsSection>
      )}

      {/* Show mapping tables for fields with BCF values */}
      {fieldsWithValues.length > 0 ? (
        <MappingSection>
          {fieldsWithValues.map((field) => {
            const mapping = (mappingConfigs as any)[field.key];
            const defaults = (availableDefaults as any)?.[field.defaultKey] || [];
            const isIgnored = mapping?.isIgnored || false;

            return (
              <div key={field.key}>
                <MappingTable
                  fieldType={field.key}
                  fieldLabel={field.label}
                  fieldDescription={field.description}
                  mapping={mapping}
                  availableValues={defaults}
                  isIgnored={isIgnored}
                  canBeIgnored={!field.required}
                  required={field.required}
                  onIgnoreChange={(isIgnored) => onIgnoreChange?.(field.key, isIgnored)}
                  onMappingChange={(bcfValue, coordlyValue) =>
                    onMappingChange(field.key, bcfValue, coordlyValue)
                  }
                />
              </div>
            );
          })}
        </MappingSection>
      ) : (
        <Empty description="Nenhum valor para mapear foi encontrado no arquivo BCF" />
      )}
    </Container>
  );
};

export default Step2DataMapping;
